import React, { useMemo, useCallback, useRef, useState, useContext, Fragment } from 'react';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';

import { getScenarioIcon } from 'app/model/Scenario';
import { LocalizationContext, useLocalizedStrings } from 'app/localization/Context';

import { LegacyPopup } from 'app/home/general-components/Popup/Popup';

import { ReactComponent as IconPlus } from 'images/general/icon-add.svg';
import { ReactComponent as IconPlay } from 'images/scenario/icon-play.svg';

import { ReactComponent as IconTick } from 'images/general/tick-green-bolder.svg';
import { ReactComponent as IconDC } from 'images/general/disconnect-red.svg';
import { ReactComponent as IconLoading } from 'images/general/three-dots-loading-gray.svg';
import { ReactComponent as IconSelection } from 'images/general/icon-badge-select.svg';

import './Scenarios.scss'

function Scenarios({ scenarios, scenarioReports, currentUser, dataFetched }) {
    const hs = useContext(HistoryStackContext);

    const onCreateClick = useCallback(() => {
        hs.push("/scenarios/create_or_edit", { });
    }, [hs]);

    const s = useLocalizedStrings();

    return (
        <div className="scenario-list-container">
            <div className="scenario-list-header">
                <div className="label">{s.scenarios}</div>
                <div />
                {
                    (dataFetched && currentUser.permissions.scenario.setting) ? (
                        <div className="new-button-border" onClick={onCreateClick}>
                            <div className="label">
                                {s.createScenarios}
                            </div>
                            <IconPlus className="plus-icon" />
                        </div>
                    ) : null
                }
            </div>

            {dataFetched ?
                <ScenarioList
                    marginTop={8}
                    scenarios={scenarios} scenarioReports={scenarioReports}
                    cantRun={!currentUser.permissions.scenario.run} />
                : <div className="scenario-list">
                    <div className="empty-list-cell">{s.dataIsLoading}</div>
                </div>}
        </div>
    );
}

export function ScenarioReportPopup({ scenarioReports, devices }) { // used in OverlayPages
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const rs = s.runScenario;

    const list = useMemo(() => {
        const finalList = [];

        if (scenarioReports.first !== undefined) {
            for (let i = 0; i < scenarioReports.first.devicesReport.length; i++) {
                const deviceReport = scenarioReports.first.devicesReport[i];
                finalList.push(
                    <Fragment
                        key={`scenario_report_${String(scenarioReports.first.reportId)}_device_${deviceReport.mac}`}>
                        {
                            (deviceReport.status === undefined) ? <IconLoading className="icon" /> :
                                (deviceReport.status === true) ? <IconTick className="icon" /> :
                                    <IconDC className="icon" />
                        }
                        <div className="text">{deviceReport.name}</div>
                    </Fragment>
                );
            }

            for (let i = 0; i < scenarioReports.first.bindReports.length; i++) {
                const bindReport = scenarioReports.first.bindReports[i];
                finalList.push(
                    <Fragment
                        key={`scenario_report_${String(scenarioReports.first.reportId)}_bind_${bindReport.id}`}>
                        {
                            (bindReport.status === undefined) ? <IconLoading className="icon" /> :
                                (bindReport.status === true) ? <IconTick className="icon" /> : null
                        }
                        <div className="text">{bindReport.name}</div>
                    </Fragment>
                );
            }
        }

        return finalList;
    }, [scenarioReports.first]);

    return (
        <LegacyPopup
            show={scenarioReports.first !== undefined}
            dismiss={() => { }} // can not dismiss
            baseZIndex={20} name="scenario-report-popup">
            {
                (scenarioReports.first === undefined) ? null :
                    <div className="scenario-report-popup">
                        <div className="header">{rs.titleHead + scenarioReports.first.scenario.name + rs.titleTail}</div>
                        <div className="list">
                            {list}
                        </div>
                        <div className="dismiss-button" onClick={() => {
                            scenarioReports.unregister(scenarioReports.first.reportId);
                        }}>{rs.gotIt}</div>
                    </div>
            }
        </LegacyPopup>
    );
}

export function ScenarioList({
    scenarios, scenarioReports,
    selectionCallback, selectedScenarios, cantRun,
    marginTop, marginLeft, marginRight }) {
    const p = useContext(CurrentPageContext);
    const s = useLocalizedStrings();

    const scenarioElementsWithBlank = useMemo(() => {
        const _selectedScenarios = Array.isArray(selectedScenarios) ? selectedScenarios : [];

        let elements = [];
        for (let i = 0; i < scenarios.length; i++) {
            const scenario = scenarios[i];
            elements.push(
                <ScenarioCell key={"scenario_id_" + String(scenario.id)}
                    cantRun={cantRun}
                    selectionCallback={selectionCallback}
                    isSelected={_selectedScenarios.includes(scenario.id)}

                    scenario={scenario} scenarioReports={scenarioReports} />
            )
        }

        elements.push(
            <div style={{ height: 16 - 8 }} key="scenario-list-padding" />
        );

        return elements;
    }, [scenarios, scenarioReports, selectedScenarios, selectionCallback, cantRun]);

    return (
        <div 
        className={"scenario-list " + (p.inOverlayPage ? "" : "responsive")}
         style={{ marginTop: marginTop, marginLeft: marginLeft, marginRight: marginRight }}>
            {
                scenarioElementsWithBlank.length > 1 ? scenarioElementsWithBlank :
                    <div className="empty-list-cell">{s.noScenario}</div>
            }
        </div>
    );
}

export function ScenarioCell({
    scenario, scenarioReports,
    onDetailClickDisabled, cantRun,
    selectionCallback, isSelected }) {

    const scenarioIcon = getScenarioIcon(scenario.pic_id);

    const buttonRef = useRef(null);
    const [runClickActive, setRunClickActive] = useState(true);
    const registerScenarioReport = scenarioReports ? scenarioReports.register : undefined;
    const runClickH = useCallback(() => {
        if (!cantRun) {
            // console.log("selectionCallback", selectionCallback);
            if (selectionCallback) {
                selectionCallback(scenario);
            } else if (runClickActive) {
                registerScenarioReport(scenario);
                scenario.run();
                buttonRef.current.className = "scenario-cell-play-button compact flashing";
                setTimeout(() => {
                    try {
                        setRunClickActive(true);
                        buttonRef.current.className = "scenario-cell-play-button compact";
                    }
                    catch (err) {
                        console.log(err);
                    }
                }, 1500 * 3 + 100);
                setRunClickActive(false);
            }
        }
    }, [scenario, registerScenarioReport, runClickActive, selectionCallback, cantRun])

    const hs = useContext(HistoryStackContext);

    const onClick = useCallback(() => {
        if (selectionCallback) {
            selectionCallback(scenario);
        } else {
            hs.push("/scenarios/detail", scenario.id);
        }
    }, [hs, scenario, selectionCallback]);

    return (
        <div className="scenario-cell">
            {React.createElement(scenarioIcon, { className: "icon" })}
            <div className="scenario-cell-name">
                {scenario.name}
                {isSelected ? " " : ""}
                {isSelected &&
                    <IconSelection className="selection-tick" />}
            </div>
            <div />
            <div className={!cantRun ? "scenario-cell-play-button compact" : "scenario-cell-play-button compact disable"}
                ref={buttonRef} onClick={runClickH}>
                <IconPlay />
            </div>

            {/* <div className={isSelected ? "selection-panel active" : "selection-panel"}/> */}
            {isSelected ? <div className="selection-panel" /> : null}

            <div onClick={onDetailClickDisabled ? undefined : onClick} className="detail-click-overlay" />
        </div>
    )
}

export default Scenarios;
